"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { useAuth } from "@/contexts/AuthContext";
import type { ProjectAU, ActuacionesAU, IndicadoresAU } from "@/types/types";
import { projectsAUService } from "@/lib/services/projects-au.service";
import type { ProjectsAUService } from "@/lib/services/projects-au-service.interface";

export interface AUEData {
  proyectos: ProjectAU[];
  actuaciones: ActuacionesAU[];
  indicadores: IndicadoresAU[];
  isFetching: boolean;
  error: unknown;
  refetchAll: () => Promise<void>;
  refetchProyectos: () => Promise<ProjectAU[]>;
  refetchActuaciones: () => Promise<ActuacionesAU[]>;
  refetchIndicadores: () => Promise<IndicadoresAU[]>;
  deleteProyecto: (id: number) => Promise<void>;
  service: ProjectsAUService;
}

export function useAUEData(): AUEData {
  const { isLoggedIn, currentTenantId, userLang } = useAuth();
  const service = projectsAUService;
  const t = useTranslations();

  const [proyectos, setProyectos] = useState<ProjectAU[]>([]);
  const [actuaciones, setActuaciones] = useState<ActuacionesAU[]>([]);
  const [indicadores, setIndicadores] = useState<IndicadoresAU[]>([]);

  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const canFetch = isLoggedIn && !!currentTenantId;
  const tenantId = currentTenantId ? Number(currentTenantId) : null;

  const refetchProyectos = useCallback(async () => {
    if (!canFetch || !tenantId) return [];
    const data = await service.fetchProjectsByTenant({
      tenantId,
      userLang,
    });
    setProyectos(data);
    return data;
  }, [canFetch, service, tenantId, userLang]);

  const refetchActuaciones = useCallback(async () => {
    if (!canFetch || !tenantId) return [];
    const data = await service.fetchActuacionesByTenant({
      tenantId,
      userLang,
    });
    setActuaciones(data);
    return data;
  }, [canFetch, service, tenantId, userLang]);

  const refetchIndicadores = useCallback(async () => {
    if (!canFetch || !tenantId) return [];
    const data = await service.fetchIndicadoresByTenant({
      tenantId,
      userLang,
    });
    setIndicadores(data);
    return data;
  }, [canFetch, service, tenantId, userLang]);

  const refetchAll = useCallback(async () => {
    if (!canFetch || !tenantId) return;

    setIsFetching(true);
    setError(null);

    try {
      await Promise.all([
        refetchProyectos(),
        refetchActuaciones(),
        refetchIndicadores(),
      ]);
    } catch (err) {
      setError(err);

      toast.error(
        t("toast.proyecto-au.fetch-error-title", {
          default: "Error loading data",
        }),
        {
          description: t("toast.proyecto-au.fetch-error-desc", {
            default: "Please try again.",
          }),
          descriptionClassName: "!text-gray-800 dark:!text-gray-200",
        }
      );
    } finally {
      setIsFetching(false);
    }
  }, [
    canFetch,
    tenantId,
    refetchProyectos,
    refetchActuaciones,
    refetchIndicadores,
    t,
  ]);

  const deleteProyecto = useCallback(
    async (id: number) => {
      if (!canFetch) return;

      try {
        await service.deleteProject({ id, refetch: refetchAll });

        toast.success(t("toast.proyecto-au.delete-success-title"), {
          description: t("toast.proyecto-au.delete-success-desc"),
          descriptionClassName: "!text-gray-800 dark:!text-gray-200",
        });
      } catch (err) {
        toast.error(t("toast.proyecto-au.delete-error-title"), {
          description: t("toast.proyecto-au.delete-error-desc"),
          descriptionClassName: "!text-gray-800 dark:!text-gray-200",
        });

        throw err;
      }
    },
    [canFetch, refetchAll, service, t]
  );

  useEffect(() => {
    if (!canFetch || !tenantId) {
      setProyectos([]);
      setActuaciones([]);
      setIndicadores([]);
      setError(null);
      return;
    }

    void refetchAll();
  }, [canFetch, tenantId, refetchAll]);

  return useMemo(
    () => ({
      proyectos,
      actuaciones,
      indicadores,
      isFetching,
      error,
      refetchAll,
      refetchProyectos,
      refetchActuaciones,
      refetchIndicadores,
      deleteProyecto,
      service,
    }),
    [
      proyectos,
      actuaciones,
      indicadores,
      isFetching,
      error,
      refetchAll,
      refetchProyectos,
      refetchActuaciones,
      refetchIndicadores,
      deleteProyecto,
      service,
    ]
  );
}
